import { Button, Center, Flex, Spinner, Td, Tr } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { makeEvent, useEventListener } from "services/events";
import { MailingTableRow } from "..";
import { DownloadIcon, EditIcon } from "@chakra-ui/icons";
import {
  openMailingOptions,
  MailingOptionFilter
} from "components/mailing-components/mailing-options-modal";
import { createLinkAndDownload } from "utils/create-link-and-download";
import { PipelineStep } from "./pipeline-step";
import { getFilteredPipelineSteps } from "./utils/pipeline-helpers";
import { useApplicationContext } from "contexts/ApplicationContext";
import { CurrentStepData } from "./current-step-data";
import { usePipelineData } from "./hooks/use-pipeline-data";
import { useTableRowAnimation } from "./hooks/use-table-row-animation";
import { parsePipelineConfig } from "./utils/config-parser";
import { downloadMailingCsv } from "./services/pipeline-api";

export interface Pipeline {
  pipelineStage:
    | "HIGIENIZACAO"
    | "FILTRO"
    | "TELEFONIA"
    | "ENVIA_WHATS"
    | "ENVIA_DISCADORA"
    | "ENVIA_CAMPANHA_SMS"
    | "SAQUE_COMPLEMENTAR"
    | "REFIN"
    | "ENVIA_ATENDIMENTO"
    | "SIMULACAO_PORTABILIDADE"
    | "MARGEM_CONVENIO"
    | "DIGITACAO";
  pipelineStatus:
    | "FINALIZADO"
    | "EM_ANDAMENTO"
    | "NAO_INICIADO"
    | "PAUSADO"
    | "ERROR"
    | null;
  banco: string | null;
  data: string;
  pipelineItemId: number;
  pipelineItemErrorDescription?: string | null;
  pipelineTipoHigienizacao:
    | "REFIN"
    | "IN_100"
    | "SIMULACAO_PORTABILIDADE"
    | null;
  pipelineItemStatus: "CANCELADO" | "SUSPENSO" | "EM_ANDAMENTO" | null;
  higienizacaoId: number | null;
  pipelineErrorDescription: string | null;
  configJson: string;
  mailingLeadCount: number;
  higienizacaoStatusResultList?: Array<{
    total: number;
    totalCompleted: number;
    tipoHigienizacao: "REFIN" | "CONSULTA_TOP";
    finished?: boolean;
    lastError?: string;
    higienizacaoId: string;
    banco: string;
    mensagem?: string;
  }> | null;
  simulacaoStatusResult?: Array<{
    id?: number | null;
    total: number;
    totalCompleted: number;
    finished?: boolean;
    lastError?: string | null;
    totalErrors?: null | number;
    banco: string | null;
    mensagem?: string | null;
    status?: "EM_ANDAMENTO" | null;
  }> | null;
  margemConvenioStatusResultList?: {
    total: number;
    totalCompleted: number;
    totalErrors: null;
    lastError: string;
    idBanco: number;
    mensagem: null;
    status: "EM_ANDAMENTO";
  }[];
  ordem?: number | null;
  currentPipelineOrdem?: number | null;
  pipelineItemBanco: null;
}

export interface MailingOptionsAutoComplete {
  higienizacao: MailingOptionFilter;
  filters: MailingOptionFilter;
  telefonia: MailingOptionFilter;
  enviaWhats: MailingOptionFilter;
  enviaDiscadora: MailingOptionFilter;
  enviaSMS: MailingOptionFilter;
  simulacaoPortabilidade: MailingOptionFilter;
  digitacao: MailingOptionFilter;
}

export const MailingTableRowDetails = ({
  columnsLength,
  row,
  rowIndex
}: {
  row: MailingTableRow;
  columnsLength: number;
  rowIndex: number;
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { user } = useApplicationContext();

  const {
    isOpen,
    tableRow,
    internalContainer,
    onOpenTableRow,
    onCloseTableRow,
    onToggle
  } = useTableRowAnimation();

  const {
    pipelineData,
    isLoading,
    setIsLoading,
    activeStep,
    setActiveStep,
    refetch
  } = usePipelineData(row, isOpen);

  const filteredPipelineSteps = getFilteredPipelineSteps({
    modalData: pipelineData,
    user
  });

  const filtroIndex = filteredPipelineSteps.findIndex(
    (i) => i.key === "FILTRO"
  );
  const isFilterDisabled =
    row.tipoOperacao === "REFIN_CARTEIRA"
      ? activeStep !== filtroIndex
      : !pipelineData.find(
          (i) =>
            i.pipelineStage === "FILTRO" && i.pipelineStatus !== "FINALIZADO"
        ) || activeStep > filtroIndex;

  const enabledFields = parsePipelineConfig(pipelineData);

  useEffect(() => {
    if (isOpen) onCloseTableRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row.id]);

  useEffect(() => {
    if (isOpen) onOpenTableRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEventListener(`mailing-update-${row.id}`, refetch);

  useEventListener(
    `table-row`,
    (index: number) => {
      if (row.currentPipelineOrdem != null && row.pipeline == null) {
        if (index === rowIndex) {
          onToggle(refetch);
        } else {
          onCloseTableRow();
        }
      }
    },
    `${rowIndex}`
  );

  return isOpen ? (
    <Tr ref={tableRow}>
      <Td colSpan={columnsLength} padding={0}>
        <Flex
          flexDir="column"
          w="100%"
          ref={internalContainer}
          overflow="hidden"
          pos="relative"
        >
          <Flex flexDir="column" p="16px" minH="300px">
            <Flex w="100%" justifyContent="flex-end">
              <Button
                mr="8px"
                leftIcon={<DownloadIcon />}
                isLoading={isDownloading}
                onClick={async () => {
                  setIsDownloading(true);
                  try {
                    const blob = await downloadMailingCsv(row.id);
                    createLinkAndDownload(blob, `${row.name}.csv`);
                  } catch (e) {
                  } finally {
                    setIsDownloading(false);
                  }
                }}
                loadingText="Baixando"
              >
                Baixar CSV
              </Button>
              <Button
                leftIcon={<EditIcon />}
                isDisabled={isFilterDisabled}
                onClick={() => {
                  openMailingOptions({
                    ...row,
                    enabledFields: enabledFields as any
                  });
                }}
              >
                Seleção Bancos / Editar
              </Button>
            </Flex>
            <Flex flexDir="column" p="8px 0px 16px">
              <Center p="16px" h="calc(45px + 32px)">
                {filteredPipelineSteps.map(({ title, key }, index) => {
                  return (
                    <PipelineStep
                      key={key}
                      activeStep={activeStep}
                      index={index}
                      pipelineStage={key}
                      modalData={pipelineData}
                      row={row}
                      setActiveStep={setActiveStep}
                      setIsLoading={setIsLoading}
                      title={title}
                    />
                  );
                })}
              </Center>

              {isLoading ? (
                <Center inset="0" pos="absolute" bg="#fff">
                  <Spinner />
                </Center>
              ) : null}
            </Flex>
            {!isLoading &&
              filteredPipelineSteps.map(({ title, key }, index) => {
                return (
                  <CurrentStepData
                    key={key}
                    activeStep={activeStep}
                    index={index}
                    pipelineStage={key}
                    modalData={pipelineData}
                    row={row}
                  />
                );
              })}
          </Flex>
        </Flex>
      </Td>
    </Tr>
  ) : null;
};

export const openLegacyPipeline = (index: number) =>
  makeEvent("table-row", index);
